"use client"

import { useEffect } from "react"
import type { Dispatch, RefObject, SetStateAction } from "react"

// Moved out of components/booru-detail-modal.tsx: the same listener with the same dependency list (values it reads but
// does not list, such as isMediaVideo and the album page handlers, are read from the render it was attached in, as before).
export function useModalKeyboard({
  open,
  isAlbum,
  viewingAdSlot,
  currentAlbumPage,
  totalAlbumPages,
  handleAlbumPrevPage,
  handleAlbumNextPage,
  onNavigate,
  canNavigate,
  onOpenChange,
  showInfo,
  setShowInfo,
  setZoom,
  setPanX,
  setPanY,
  isMediaVideo,
  isVideo,
  videoRef,
  setIsPlaying,
}: {
  open: boolean
  isAlbum: boolean
  viewingAdSlot: number | null
  currentAlbumPage: number
  totalAlbumPages: number
  handleAlbumPrevPage: () => void
  handleAlbumNextPage: () => void
  onNavigate: ((direction: "prev" | "next") => void) | undefined
  canNavigate: { prev: boolean; next: boolean } | undefined
  onOpenChange: (open: boolean) => void
  showInfo: boolean
  setShowInfo: Dispatch<SetStateAction<boolean>>
  setZoom: Dispatch<SetStateAction<number>>
  setPanX: Dispatch<SetStateAction<number>>
  setPanY: Dispatch<SetStateAction<number>>
  isMediaVideo: boolean
  isVideo: boolean
  videoRef: RefObject<HTMLVideoElement | null>
  setIsPlaying: Dispatch<SetStateAction<boolean>>
}) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.contentEditable === "true") {
        return
      }

      if (!open) return

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault()
          if (isAlbum && (viewingAdSlot !== null || currentAlbumPage > 0)) {
            handleAlbumPrevPage()
          } else if (onNavigate && canNavigate?.prev) {
            onNavigate("prev")
          }
          break
        case "ArrowRight":
          e.preventDefault()
          if (isAlbum && (viewingAdSlot !== null || currentAlbumPage < totalAlbumPages - 1)) {
            handleAlbumNextPage()
          } else if (onNavigate && canNavigate?.next) {
            onNavigate("next")
          }
          break
        case "Escape":
          onOpenChange(false)
          break
        case "i":
        case "I":
          setShowInfo(!showInfo)
          break
        case "+":
        case "=":
          setZoom((z) => Math.min(z + 0.25, 5))
          break
        case "-":
          setZoom((z) => Math.max(z - 0.25, 0.1))
          break
        case "0":
          setZoom(1)
          setPanX(0)
          setPanY(0)
          break
        case " ":
          if (isMediaVideo && videoRef.current) {
            e.preventDefault()
            if (videoRef.current.paused) {
              // Unguarded — a fast double-tap of Space calls play() then
              // pause() before the first play() promise settles, which
              // rejects with an uncaught "AbortError: play() request was
              // interrupted by pause()" (surfaced live in the Next.js error
              // overlay). Harmless race, just needs to not throw.
              videoRef.current.play()?.catch(() => {})
              setIsPlaying(true)
            } else {
              videoRef.current.pause()
              setIsPlaying(false)
            }
          }
          break
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [open, onNavigate, canNavigate, showInfo, isVideo, isAlbum, currentAlbumPage, totalAlbumPages, viewingAdSlot])
}
